"use client";

import type { FormBuilderHandle, IFormBuilderSchema } from "@atlas/composite";
import { Card, CardContent, CardTitle, Label } from "@atlas/core";
import { useFormBuilder, useTranslation } from "@atlas/sdk";
import type { IHubCatalog } from "@models";
import { FormGenerator } from "@modules";
import type { RefObject } from "react";

interface IUserIntegrationTuningStepProps {
  catalog?: IHubCatalog | null;
  isEdit: boolean;
  onSchemaChange: (
    schema: IFormBuilderSchema | null,
    generatedJson?: string,
    isDirty?: boolean,
  ) => void;
  formBuilderRef?: RefObject<FormBuilderHandle | null>;
  onSubmit?: (schema: IFormBuilderSchema, generatedJson: string) => void;
}

export function UserIntegrationTuningStep({
  catalog,
  isEdit,
  onSchemaChange,
  formBuilderRef,
  onSubmit,
}: IUserIntegrationTuningStepProps) {
  const formId = isEdit ? (catalog?.formId ?? 0) : 0;
  const { t } = useTranslation();
  const { data: formData, isLoading: formLoading } = useFormBuilder(formId, {
    enabled: isEdit && formId > 0,
  });

  return (
    <Card
      variant="content"
      className="border-0 shadow-none flex-1 min-h-0"
      isLoading={Boolean(isEdit && formId > 0 && formLoading)}>
      <CardTitle className="mb-1">
        {t("hub.create_catalog.user_integration_tuning", {
          defaultValue: "User Integration Tuning",
        })}
      </CardTitle>
      <Label className="text-sm text-muted-foreground mb-4">
        {t("hub.create_catalog.user_integration_tuning_description", {
          defaultValue:
            "Design the configuration form that users will fill out when installing this catalog. Add fields to collect necessary settings, credentials, or preferences.",
        })}
      </Label>
      <CardContent className="p-0 min-h-0">
        <FormGenerator
          ref={formBuilderRef}
          formData={isEdit && formId > 0 ? formData : null}
          isEdit={isEdit}
          onSchemaChange={onSchemaChange}
          onSubmit={onSubmit}
        />
      </CardContent>
    </Card>
  );
}
